import { computed, useHost, onBeforeUnmount, type ComputedRef, type Ref, type ToRefs } from "vue";
import { useSharedState } from "composables/useSharedState";
import { selectionHelpers } from "composables/useCollectionItem";
import { useDropdownBase } from "composables/useDropdownBase";
import type { ComboboxProps } from "./combobox.types";
import { comboboxClasses } from "./combobox.styles";
import type { AcceptableValue } from "@/runtime/types/utils";
import { scrollItemIntoListboxView } from "@/runtime/shared/scroll";

const COMBOBOX_TYPE = "Combobox";
const COMBOBOX_ITEM_TYPE = "ComboboxItem";

/** Model value type that can be single, multiple, or undefined */
type ModelValueType = AcceptableValue | AcceptableValue[] | undefined;

/** Item metadata stored in registry */
interface ItemMetadata {
  label: string;
  textValue: string;
  disabled: boolean;
}

/** Item registry map type */
type ItemRegistryMap = Map<AcceptableValue, ItemMetadata>;

/** Filtered items cache entry */
interface FilteredItemsCacheEntry {
  searchTerm: string;
  filterDisabled: boolean;
  result: AcceptableValue[];
  version: number;
}

const itemRegistry = new WeakMap<Element, ItemRegistryMap>();
const filteredItemsCache = new WeakMap<Element, FilteredItemsCacheEntry>();
const registryVersions = new WeakMap<Element, number>();
const inputElementRegistry = new WeakMap<Element, Element>();

export const registerComboboxInputElement = (comboboxElement: Element, inputElement: Element): void => {
  inputElementRegistry.set(comboboxElement, inputElement);
};

export const unregisterComboboxInputElement = (comboboxElement: Element): void => {
  inputElementRegistry.delete(comboboxElement);
};

export const getComboboxInputElement = (comboboxElement: Element): Element | undefined => {
  return inputElementRegistry.get(comboboxElement);
};

export const registerCombobox = (element: Element): void => {
  itemRegistry.set(element, new Map());
  registryVersions.set(element, 0);
};

export const unregisterCombobox = (element: Element): void => {
  itemRegistry.delete(element);
  filteredItemsCache.delete(element);
  registryVersions.delete(element);
};

export const registerComboboxItem = (
  comboboxElement: Element,
  value: AcceptableValue,
  label: string,
  textValue?: string,
  disabled = false,
): void => {
  const registry = itemRegistry.get(comboboxElement);
  if (registry) {
    registry.set(value, {
      label,
      textValue: textValue ?? label,
      disabled,
    });
    const version = registryVersions.get(comboboxElement) ?? 0;
    registryVersions.set(comboboxElement, version + 1);
  }
};

export const unregisterComboboxItem = (comboboxElement: Element, value: AcceptableValue): void => {
  const registry = itemRegistry.get(comboboxElement);
  if (registry) {
    registry.delete(value);
    const version = registryVersions.get(comboboxElement) ?? 0;
    registryVersions.set(comboboxElement, version + 1);
  }
};

export const getItemLabel = (comboboxElement: Element, value: AcceptableValue): string | undefined => {
  const registry = itemRegistry.get(comboboxElement);
  return registry?.get(value)?.label;
};

export const getRegisteredItems = (comboboxElement: Element): ItemRegistryMap | undefined => {
  return itemRegistry.get(comboboxElement);
};

export interface UseComboboxOptions {
  itemValue?: Ref<AcceptableValue | undefined>;
}

export interface UseComboboxReturn {
  // Combobox context
  comboboxUid: ComputedRef<number | undefined>;
  comboboxProps: ComputedRef<ComboboxProps | undefined>;
  comboboxElement: ComputedRef<Element | null>;

  // Item context
  isSelected: ComputedRef<boolean>;
  isHighlighted: ComputedRef<boolean>;
  isDisabled: ComputedRef<boolean>;
  isFiltered: ComputedRef<boolean>;

  // Open state
  isOpen: ComputedRef<boolean>;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // Selection
  select: (value?: AcceptableValue) => void;
  deselect: (value?: AcceptableValue) => void;
  toggleSelection: (value?: AcceptableValue) => void;
  setModelValue: (value: ModelValueType) => void;

  // Search/Filter
  searchTerm: ComputedRef<string>;
  setSearchTerm: (term: string) => void;
  filteredItems: ComputedRef<AcceptableValue[]>;
  hasFilteredItems: ComputedRef<boolean>;

  // Highlight (for keyboard navigation)
  highlightedValue: ComputedRef<AcceptableValue | undefined>;
  setHighlightedValue: (value: AcceptableValue | undefined) => void;
  scrollItemIntoView: (value: AcceptableValue) => void;

  // Focus (triggers item self-focus)
  focusValue: ComputedRef<AcceptableValue | undefined>;
  setFocusValue: (value: AcceptableValue | undefined) => void;
  shouldFocus: ComputedRef<boolean>;

  // Value
  modelValue: ComputedRef<AcceptableValue | AcceptableValue[] | undefined>;
  displayValue: ComputedRef<string>;
}

const containsText = (text: string, query: string): boolean => {
  return text.toLowerCase().includes(query.toLowerCase());
};

const getFilteredItemsCached = (el: Element, searchTerm: string, filterDisabled: boolean): AcceptableValue[] => {
  const registry = getRegisteredItems(el);
  if (!registry) return [];

  const currentVersion = registryVersions.get(el) ?? 0;
  const cached = filteredItemsCache.get(el);

  // Return cached result if inputs haven't changed
  if (
    cached &&
    cached.searchTerm === searchTerm &&
    cached.filterDisabled === filterDisabled &&
    cached.version === currentVersion
  ) {
    return cached.result;
  }

  // Calculate new result
  // Filter out disabled items, then apply search term if applicable
  let enabledEntries = [...registry.entries()].filter((entry) => !entry[1].disabled);
  if (!filterDisabled && searchTerm)
    enabledEntries = enabledEntries.filter((entry) => containsText(entry[1].textValue, searchTerm));
  const result: AcceptableValue[] = enabledEntries.map((entry) => entry[0]);

  // Cache the result
  filteredItemsCache.set(el, {
    searchTerm,
    filterDisabled,
    result,
    version: currentVersion,
  });

  return result;
};

/**
 * Finds parent Combobox context via component tree/registry
 * using useComponentContext to identify this component and traverse up.
 */
/** Build combobox-specific computed properties. */
const buildComboboxComputeds = (base: ReturnType<typeof useDropdownBase>) => {
  const comboboxProps = base.parentProps as ComputedRef<ComboboxProps | undefined>;
  const searchTerm = computed(() => comboboxProps.value?.searchTerm ?? "");

  const isFiltered = computed(() => {
    const value = base.currentItemValue.value;
    if (value === undefined) return true;
    const term = searchTerm.value;
    if (comboboxProps.value?.filterDisabled || !term) return true;
    const el = base.parentElement.value;
    if (!el) return true;
    const item = getRegisteredItems(el)?.get(value);
    return !item || containsText(item.textValue, term);
  });

  const filteredItems = computed((): AcceptableValue[] => {
    const el = base.parentElement.value;
    if (!el) return [];
    return getFilteredItemsCached(el, searchTerm.value, comboboxProps.value?.filterDisabled ?? false);
  });

  return {
    comboboxProps,
    searchTerm,
    isFiltered,
    filteredItems,
    hasFilteredItems: computed(() => filteredItems.value.length > 0),
  };
};

/** Scroll a combobox item into view by its value. */
const scrollComboboxItemIntoView = (parentElement: ComputedRef<Element | null>, targetValue: AcceptableValue): void => {
  const el = parentElement.value;
  if (!el || targetValue === undefined) return;
  for (const item of el.querySelectorAll("e-combobox-item")) {
    if ((item as any).value === targetValue) {
      scrollItemIntoListboxView(item as HTMLElement);
      break;
    }
  }
};

/** Build combobox-specific action functions. */
const buildComboboxActions = (
  base: ReturnType<typeof useDropdownBase>,
  comboboxProps: ComputedRef<ComboboxProps | undefined>,
) => {
  const setSearchTerm = (term: string) => base.setProp("searchTerm", term);

  const close = () => {
    base.setProp("open", false);
    if (comboboxProps.value?.resetSearchTermOnBlur !== false) setSearchTerm("");
  };

  const toggle = () => (base.isOpen.value ? close() : base.open());

  const select = (value?: AcceptableValue) => {
    const itemValue = base.resolveItemValue(value);
    const props = comboboxProps.value;
    if (!props || props.disabled || itemValue === undefined || base.parentUid.value === undefined) return;
    base.setProp("modelValue", selectionHelpers.select(itemValue, props.modelValue, props.multiple ?? false));
    if (props.resetSearchTermOnSelect !== false) setSearchTerm("");
    if (!props.multiple) close();
  };

  const deselect = (value?: AcceptableValue) => {
    const itemValue = base.resolveItemValue(value);
    const props = comboboxProps.value;
    if (!props || props.disabled || itemValue === undefined || base.parentUid.value === undefined) return;
    base.setProp("modelValue", selectionHelpers.deselect(itemValue, props.modelValue, props.multiple ?? false));
  };

  const toggleSelection = (value?: AcceptableValue) => {
    const itemValue = base.resolveItemValue(value);
    if (itemValue === undefined) return;
    const props = comboboxProps.value;
    if (!props || props.disabled) return;
    const behavior = props.multiple ? "toggle" : "replace";
    base.setProp("modelValue", selectionHelpers.toggle(itemValue, props.modelValue, props.multiple ?? false, behavior));
    if (props.resetSearchTermOnSelect !== false) setSearchTerm("");
    if (!props.multiple) close();
  };

  const setModelValue = (value: AcceptableValue | AcceptableValue[] | undefined) => {
    if (base.parentUid.value === undefined) return;
    const props = comboboxProps.value;
    if (!props || props.disabled) return;
    base.setProp("modelValue", value);
  };

  const scrollItemIntoView = (v: AcceptableValue) => scrollComboboxItemIntoView(base.parentElement, v);
  return { close, toggle, scrollItemIntoView, select, deselect, toggleSelection, setModelValue, setSearchTerm };
};

export const useCombobox = (options?: UseComboboxOptions): UseComboboxReturn => {
  const base = useDropdownBase(
    {
      parentType: COMBOBOX_TYPE,
      itemType: COMBOBOX_ITEM_TYPE,
      getItemLabel,
    },
    options,
  );

  const computeds = buildComboboxComputeds(base);
  const actions = buildComboboxActions(base, computeds.comboboxProps);

  return {
    comboboxUid: base.parentUid,
    comboboxProps: computeds.comboboxProps,
    comboboxElement: base.parentElement,
    isSelected: base.isSelected,
    isHighlighted: base.isHighlighted,
    isDisabled: base.isDisabled,
    isFiltered: computeds.isFiltered,
    isOpen: base.isOpen,
    open: base.open,
    ...actions,
    searchTerm: computeds.searchTerm,
    filteredItems: computeds.filteredItems,
    hasFilteredItems: computeds.hasFilteredItems,
    highlightedValue: base.highlightedValue,
    setHighlightedValue: base.setHighlightedValue,
    focusValue: base.focusValue,
    setFocusValue: base.setFocusValue,
    shouldFocus: base.shouldFocus,
    modelValue: base.modelValue,
    displayValue: base.displayValue,
  };
};

export interface UseComboboxSetupOptions {
  props: Readonly<ComboboxProps>;
}

export interface UseComboboxSetupReturn {
  state: ToRefs<ComboboxProps>;
  isMultiple: ComputedRef<boolean>;
  classes: ComputedRef<unknown>;
}

export const useComboboxSetup = (options: UseComboboxSetupOptions): UseComboboxSetupReturn => {
  const { props } = options;
  const host = useHost();

  const state = useSharedState(props);
  const isMultiple = computed(() => state.multiple?.value ?? props.multiple ?? false);

  if (host) {
    registerCombobox(host);
  }

  onBeforeUnmount(() => {
    if (host) {
      unregisterCombobox(host);
    }
  });

  /**
   * Focus the combobox input - attached to host for Field label clicks
   */
  const focus = () => {
    if (host) {
      const combobox = host.querySelector('[role="combobox"]') as HTMLElement | null;
      combobox?.focus();
    }
  };

  // Attach focus method to the custom element host for DOM access
  if (host) {
    (host as any).focus = focus;
  }

  const classes = computed(() =>
    comboboxClasses({
      disabled: state.disabled?.value ? "true" : "false",
      block: (state.block?.value ?? props.block) ? "true" : "false",
    }),
  );

  return {
    state,
    isMultiple,
    classes,
  };
};
